/**
 * contextBuilders — Pure text formatters that turn TanStack Query payloads
 * into concise pageContext strings for the Skerpa system prompt. Extracted
 * from SkerpaAutoContext to keep that component under the 300-line cap.
 *
 * Output shapes are also parsed by autoGreet.ts (buildNudgeCopy), so changing
 * the leading summary sentences ("Personas: N total", "Competitor Map: ...",
 * "Market Segments: N defined") will break ViewNudge labels and the auto-greet
 * intros — update both together.
 */

import type { PersonaWorkshopSession, PersonaData, Competitor } from '@ispot/shared'
import { migratePersonaData } from '@ispot/shared'
import type { InputSource, Facet } from '@/types/interviewer'

export function buildPersonaContext(
  sessions: PersonaWorkshopSession[],
  segmentNames?: string[],
  activeSessionId?: string | null,
): string {
  const lines = [`Personas: ${sessions.length} total\n`]

  for (const s of sessions.slice(0, 8)) {
    const data = migratePersonaData(s.personaData as PersonaData)
    const jtbds = data.jtbds || []
    const isActive = s.id === activeSessionId
    const totalPains = jtbds.reduce((sum, j) => sum + j.pains.length, 0)
    const totalGains = jtbds.reduce((sum, j) => sum + j.gains.length, 0)

    const parts = [
      data.name || 'Unnamed',
      data.role ? `(${data.role})` : null,
      jtbds.length ? `${jtbds.length} JTBDs` : null,
      totalPains ? `${totalPains} pains` : null,
      totalGains ? `${totalGains} gains` : null,
    ].filter(Boolean)

    lines.push(`  - ${parts.join(' · ')}${isActive ? ' [CURRENTLY EDITING]' : ''}`)

    if (isActive) {
      // Full detail for the persona being edited so Skerpa can audit it
      if (data.archetype) lines.push(`    Archetype: ${data.archetype}`)
      if (data.context) lines.push(`    Context: ${data.context}`)
      if (data.quote) lines.push(`    Quote: "${data.quote}"`)
      if (data.currentTools?.length) lines.push(`    Tools: ${data.currentTools.join(', ')}`)
      if (data.constraints?.length) lines.push(`    Constraints: ${data.constraints.join(', ')}`)
      for (const j of jtbds) {
        lines.push(`    JTBD: ${j.title || '(untitled)'}`)
        if (j.functional) lines.push(`      Functional: ${j.functional}`)
        if (j.emotional) lines.push(`      Emotional: ${j.emotional}`)
        if (j.social) lines.push(`      Social: ${j.social}`)
        if (j.pains.length) {
          lines.push(`      Pains (${j.pains.length}):`)
          for (const p of j.pains) lines.push(`        • [${p.severity || '?'}] ${p.description}`)
        }
        if (j.gains.length) {
          lines.push(`      Gains (${j.gains.length}):`)
          for (const g of j.gains) lines.push(`        • [${g.importance || '?'}] ${g.description}`)
        }
      }
    } else {
      for (const j of jtbds.slice(0, 3)) {
        if (j.title) {
          const hasProblem = !!j.customerProblem
          lines.push(`      JTBD: ${j.title}${hasProblem ? ' [synthesized]' : ''}`)
        }
      }
    }
  }

  if (sessions.length > 8) lines.push(`  ... and ${sessions.length - 8} more`)

  // Surface segments that have no linked persona so Skerpa can suggest creating them
  if (segmentNames && segmentNames.length > 0) {
    const coveredSegments = new Set(
      sessions
        .map(s => (migratePersonaData(s.personaData as PersonaData).segment || '').trim().toLowerCase())
        .filter(Boolean),
    )
    const uncovered = segmentNames.filter(name => !coveredSegments.has(name.toLowerCase()))
    if (uncovered.length > 0) {
      lines.push(`\nSegments without a linked persona: ${uncovered.join(', ')}`)
    }
  }

  return lines.join('\n')
}

export function buildInterviewContext(source: InputSource): string {
  const lines: string[] = []
  lines.push(`Interview: ${source.title}`)
  if (source.interviewee) lines.push(`Interviewee: ${source.interviewee}`)
  if (source.personaName) lines.push(`Linked persona: ${source.personaName}`)
  lines.push(`Status: ${source.status}`)
  lines.push(`Type: ${source.sourceType}`)

  const scores = source.scores
  if (scores) {
    const scoreParts: string[] = []
    if (scores.evidenceStrength) scoreParts.push(`Evidence: ${scores.evidenceStrength}/5`)
    if (scores.contextRichness) scoreParts.push(`Context: ${scores.contextRichness}/5`)
    if (scores.momTestScore) scoreParts.push(`Mom Test: ${scores.momTestScore}/5`)
    if (scoreParts.length > 0) lines.push(`Scores: ${scoreParts.join(' · ')}`)
  }

  if (source.qualityNotes?.length > 0) {
    lines.push(`\nQuality Notes:`)
    for (const note of source.qualityNotes.slice(0, 5)) {
      lines.push(`  - [${note.type}] ${note.message}`)
    }
  }

  const activeFacets = source.facets.filter((f: Facet) => !f.dismissed)
  if (activeFacets.length > 0) {
    lines.push(`\nExtracted Facets (${activeFacets.length}):`)
    const byType = new Map<string, Facet[]>()
    for (const f of activeFacets) { if (!byType.has(f.type)) byType.set(f.type, []); byType.get(f.type)!.push(f) }
    for (const [type, facets] of byType) {
      lines.push(`  ${type} (${facets.length}):`)
      for (const f of facets.slice(0, 4)) lines.push(`    - ${f.content}`)
      if (facets.length > 4) lines.push(`    ... and ${facets.length - 4} more`)
    }
  }

  if (source.rawContent) {
    const excerpt = source.rawContent.slice(0, 800)
    lines.push(`\nTranscript excerpt:\n${excerpt}${source.rawContent.length > 800 ? '\n[... truncated]' : ''}`)
  }

  return lines.join('\n')
}

export function buildJourneyContext(
  view: string,
  framework: { goals: Array<{ id: string; name: string; shortDescription?: string }> },
  journey: { goalId: string | null; currentPhaseId: string; phaseProgress: Record<string, number>; completedActivities: string[]; completedArtifacts: string[] },
  getCurrentPhase: () => { id: string; name: string; objective: string; activities: Array<{ id: string; name: string; description?: string }> } | null,
  getActivePhases: () => Array<{ id: string; name: string; domain: string; order: number }>,
  getDomainScores: () => { market: number; solution: number; business: number },
  getOverallProgress: () => number,
  isActivityCompleted: (id: string) => boolean,
): string {
  const lines: string[] = []
  const scores = getDomainScores()
  const overall = getOverallProgress()
  const currentPhase = getCurrentPhase()
  const phases = getActivePhases()

  const goal = journey.goalId ? framework.goals.find(g => g.id === journey.goalId) : null
  lines.push(`Journey Goal: ${goal ? goal.name : 'Not selected'}`)
  lines.push(`Overall progress: ${overall}%`)
  lines.push(`Domain scores: Market ${scores.market}% · Solution ${scores.solution}% · Business ${scores.business}%`)

  if (phases.length > 0) {
    lines.push(`\nPhases:`)
    for (const p of phases) {
      const pct = journey.phaseProgress[p.id] || 0
      const isCurrent = p.id === journey.currentPhaseId
      lines.push(`  ${p.name} (${p.domain}) — ${pct}%${isCurrent ? ' ← current' : ''}`)
    }
  }

  if (currentPhase) {
    const activities = currentPhase.activities || []
    const done = activities.filter(a => isActivityCompleted(a.id))
    const remaining = activities.filter(a => !isActivityCompleted(a.id))

    lines.push(`\nCurrent Phase: ${currentPhase.name}`)
    lines.push(`Objective: ${currentPhase.objective}`)
    lines.push(`Activities: ${done.length}/${activities.length} completed`)

    if (remaining.length > 0) {
      lines.push(`\nNext activities to complete:`)
      for (const a of remaining.slice(0, 5)) {
        lines.push(`  [ ] ${a.name}${a.description ? ` — ${a.description}` : ''}`)
      }
    }
  }

  if (view === 'analysis') {
    const weakest = Object.entries(scores).sort(([, a], [, b]) => a - b)[0]
    if (weakest) lines.push(`\nWeakest domain: ${weakest[0]} at ${weakest[1]}% — focus evidence gathering here.`)
  }

  return lines.join('\n')
}

export function buildCompetitorContext(competitors: Competitor[]): string {
  const confirmed = competitors.filter(c => !c.suggestedByAi)
  const suggested = competitors.filter(c => c.suggestedByAi)
  const byCategory = new Map<string, string[]>()

  for (const c of confirmed) {
    const cat = c.category || 'Uncategorized'
    if (!byCategory.has(cat)) byCategory.set(cat, [])
    byCategory.get(cat)!.push(`${c.name}${c.type ? ` (${c.type})` : ''}`)
  }

  const lines = [`Competitor Map: ${confirmed.length} tracked, ${suggested.length} suggested\n`]
  for (const [cat, names] of byCategory) lines.push(`${cat}: ${names.join(', ')}`)
  lines.push(`\nDo NOT suggest competitors that are already listed above.`)
  return lines.join('\n')
}

export function buildSegmentationContext(data: {
  canvas?: { beachheadSegmentId?: string | null } | null
  segments?: Array<{ id: string; name: string; status: string; sector?: string; vertical?: string | null; weightedScore?: number }>
  personas?: Array<{ id: string; name: string }>
}): string {
  const segments = data.segments || []
  const personas = data.personas || []
  if (segments.length === 0) return 'User is on Market Segmentation — no segments defined yet. They can create segments manually or generate them from personas.'

  const lines = [`Market Segments: ${segments.length} defined, ${personas.length} linked personas`]
  const beachheadId = data.canvas?.beachheadSegmentId
  if (beachheadId) {
    const bh = segments.find(s => s.id === beachheadId)
    if (bh) lines.push(`Beachhead: ${bh.name}`)
  } else {
    lines.push('Beachhead: not selected')
  }

  const sorted = [...segments].sort((a, b) => (b.weightedScore || 0) - (a.weightedScore || 0))
  lines.push('\nTop segments by weighted score:')
  for (const s of sorted.slice(0, 8)) {
    const score = typeof s.weightedScore === 'number' ? s.weightedScore.toFixed(1) : '?'
    const loc = [s.sector, s.vertical].filter(Boolean).join(' · ')
    lines.push(`  - ${s.name} — score ${score} · ${s.status}${loc ? ` · ${loc}` : ''}`)
  }
  if (segments.length > 8) lines.push(`  ... and ${segments.length - 8} more`)
  return lines.join('\n')
}
